import React, { useEffect, useMemo, useState } from 'react';
import {
  GitMerge,
  Link2,
  XCircle,
  Plus,
  Trash2,
  RefreshCcw,
  Upload,
} from 'lucide-react';
import { adminUsersAPI } from '../api';

const STATUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'Not Actioned' },
  { key: 'introduced', label: 'Introduced' },
  { key: 'connected', label: 'Connected' },
  { key: 'dismissed', label: 'Dismissed' },
];

const Overlap = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [totals, setTotals] = useState({ advertisers: 0, affiliates: 0, suggestions: 0 });
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [bulkCategories, setBulkCategories] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioningKey, setActioningKey] = useState('');
  const [categorySaving, setCategorySaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [notesDrafts, setNotesDrafts] = useState({});

  const pairKey = (item) => `${item.advertiser.id}:${item.affiliate.id}`;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [overlapResponse, categoriesResponse] = await Promise.all([
        adminUsersAPI.overlap(),
        adminUsersAPI.overlapCategories(),
      ]);

      const overlapData = overlapResponse?.data || overlapResponse;
      const categoryData = categoriesResponse?.data || categoriesResponse;
      const rows = overlapData?.suggestions || [];

      setSuggestions(rows);
      setTotals(overlapData?.totals || { advertisers: 0, affiliates: 0, suggestions: 0 });
      setCategories(categoryData?.categories || []);

      setNotesDrafts((prev) => {
        const next = { ...prev };
        rows.forEach((item) => {
          const key = pairKey(item);
          if (next[key] === undefined) {
            next[key] = item.introduction?.notes || `Matched overlap score ${item.overlap_score}`;
          }
        });
        return next;
      });
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredSuggestions = useMemo(() => {
    if (statusFilter === 'all') return suggestions;
    if (statusFilter === 'new') return suggestions.filter((item) => !item.introduction);
    return suggestions.filter((item) => item.introduction?.status === statusFilter);
  }, [suggestions, statusFilter]);

  const upsertIntroduction = async (item, status) => {
    const key = pairKey(item);

    try {
      setActioningKey(`${key}:${status}`);

      if (item.introduction?.id) {
        await adminUsersAPI.updateIntroduction(item.introduction.id, {
          status,
          notes: notesDrafts[key] || '',
        });
      } else {
        await adminUsersAPI.createIntroduction({
          advertiser_id: item.advertiser.id,
          affiliate_id: item.affiliate.id,
          shared_categories: item.shared_categories,
          status,
          notes: notesDrafts[key] || `Matched overlap score ${item.overlap_score}`,
        });
      }

      await fetchData();
    } catch (e) {
      window.alert(e.message || 'Failed to update connection status');
    } finally {
      setActioningKey('');
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    const value = newCategory.trim();
    if (!value) return;

    try {
      setCategorySaving(true);
      await adminUsersAPI.createOverlapCategory(value);
      setNewCategory('');
      await fetchData();
    } finally {
      setCategorySaving(false);
    }
  };

  const importCategories = async (e) => {
    e.preventDefault();
    const values = bulkCategories
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (!values.length) return;

    try {
      setBulkSaving(true);
      await Promise.all(values.map((name) => adminUsersAPI.createOverlapCategory(name)));
      setBulkCategories('');
      await fetchData();
    } finally {
      setBulkSaving(false);
    }
  };

  const removeCategory = async (categoryId) => {
    await adminUsersAPI.deleteOverlapCategory(categoryId);
    await fetchData();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">Overlap Opportunities</h2>
          <p className="text-slate-400 mt-2">Find matching traffic and offers, then introduce, connect, or dismiss each pair</p>
        </div>
        <button onClick={fetchData} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg transition-colors inline-flex items-center">
          <RefreshCcw className="w-4 h-4 mr-2" />Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="admin-glass rounded-xl p-5 border border-slate-700/50">
          <p className="text-slate-400 text-sm">Advertisers with sign-up data</p>
          <p className="text-2xl font-bold text-slate-100 mt-1">{totals.advertisers}</p>
        </div>
        <div className="admin-glass rounded-xl p-5 border border-slate-700/50">
          <p className="text-slate-400 text-sm">Affiliates with sign-up data</p>
          <p className="text-2xl font-bold text-slate-100 mt-1">{totals.affiliates}</p>
        </div>
        <div className="admin-glass rounded-xl p-5 border border-slate-700/50">
          <p className="text-slate-400 text-sm">Suggested overlaps</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{totals.suggestions}</p>
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-slate-100 mb-4">Category Taxonomy</h3>

          <form onSubmit={addCategory} className="flex flex-col sm:flex-row gap-3 mb-3">
            <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} type="text" placeholder="Add category (e.g. Insurance)" className="flex-1 px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400" />
            <button type="submit" disabled={categorySaving} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-lg transition-colors flex items-center justify-center">
              <Plus className="w-4 h-4 mr-2" />Add Category
            </button>
          </form>

          <form onSubmit={importCategories} className="flex flex-col gap-3 mb-4">
            <textarea value={bulkCategories} onChange={(e) => setBulkCategories(e.target.value)} rows={3} placeholder="Bulk add categories, comma or newline separated" className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400" />
            <button type="submit" disabled={bulkSaving} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded-lg transition-colors inline-flex items-center justify-center">
              <Upload className="w-4 h-4 mr-2" />Import Categories
            </button>
          </form>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <span key={category.id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-slate-200 bg-slate-800 border border-slate-700">
                {category.name}
                <button onClick={() => removeCategory(category.id)} className="ml-2 text-slate-400 hover:text-red-400" title="Remove category">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <h3 className="text-xl font-bold text-slate-100 flex items-center">
              <GitMerge className="w-5 h-5 mr-2" />Suggested Connections
            </h3>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((item) => (
                <button
                  key={item.key}
                  onClick={() => setStatusFilter(item.key)}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${statusFilter === item.key ? 'border-red-600/60 bg-red-600/20 text-red-300' : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4" />
                <p className="text-slate-400">Calculating overlap...</p>
              </div>
            </div>
          ) : error ? (
            <div className="h-80 flex items-center justify-center">
              <div className="text-center">
                <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
                <p className="text-red-400 text-lg">Error loading overlap</p>
                <p className="text-slate-500">{error}</p>
              </div>
            </div>
          ) : filteredSuggestions.length === 0 ? (
            <div className="h-80 flex items-center justify-center border-2 border-dashed border-slate-700 rounded-lg">
              <div className="text-center">
                <Link2 className="w-14 h-14 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-lg">No overlap suggestions in this filter</p>
                <p className="text-slate-500">Try switching status filters or collect more category data.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredSuggestions.map((item) => {
                const key = pairKey(item);
                const introStatus = item.introduction?.status || 'new';
                const advertiserUnique = item.advertiser.overlap_categories.filter((category) => !item.shared_categories.includes(category));
                const affiliateUnique = item.affiliate.overlap_categories.filter((category) => !item.shared_categories.includes(category));

                return (
                  <div key={key} className="rounded-xl border border-slate-700 bg-slate-800/30 p-4 space-y-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div>
                        <p className="text-slate-100 font-semibold">{item.advertiser.name} ↔ {item.affiliate.name}</p>
                        <p className="text-slate-400 text-sm">Overlap score: {item.overlap_score}</p>
                        <p className="text-slate-500 text-sm">Status: {introStatus}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => upsertIntroduction(item, 'introduced')} disabled={actioningKey === `${key}:introduced`} className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm">Introduce</button>
                        <button onClick={() => upsertIntroduction(item, 'connected')} disabled={actioningKey === `${key}:connected`} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm">Connect</button>
                        <button onClick={() => upsertIntroduction(item, 'dismissed')} disabled={actioningKey === `${key}:dismissed`} className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 text-sm">Dismiss</button>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3 text-sm">
                      <div className="rounded-lg bg-slate-900/50 border border-slate-700 p-3">
                        <p className="text-slate-500">Shared categories</p>
                        <p className="mt-1 text-slate-200">{item.shared_categories.join(', ') || '-'}</p>
                      </div>
                      <div className="rounded-lg bg-slate-900/50 border border-slate-700 p-3">
                        <p className="text-slate-500">Advertiser unique</p>
                        <p className="mt-1 text-slate-200">{advertiserUnique.join(', ') || '-'}</p>
                      </div>
                      <div className="rounded-lg bg-slate-900/50 border border-slate-700 p-3">
                        <p className="text-slate-500">Affiliate unique</p>
                        <p className="mt-1 text-slate-200">{affiliateUnique.join(', ') || '-'}</p>
                      </div>
                    </div>

                    <textarea value={notesDrafts[key] || ''} onChange={(e) => setNotesDrafts((prev) => ({ ...prev, [key]: e.target.value }))} rows={3} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400" placeholder="Notes for this introduction" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Overlap;
